import React from 'react';
import { VisualAid } from '../../types';
import { MetricStairsCompact } from './MetricStairsCompact';
import { FractionPizza } from './FractionPizza';
import { FractionVisual } from './FractionVisual';
import { GeoGrid } from './GeoGrid';
import { NumberLine } from './NumberLine';
import { RatioTable } from './RatioTable';
import { RuleCard } from './RuleCard';
import { MoneyVisual } from './MoneyVisual';
import { ColumnVisual } from './ColumnVisual';
import { AlertTriangle } from 'lucide-react';
interface VisualAidFactoryProps {
  visualAid: VisualAid;
  isCorrect?: boolean;
}
export function VisualAidFactory({
  visualAid,
  isCorrect
}: VisualAidFactoryProps) {
  // FIXED: Enhanced error handling with detailed logging
  if (!visualAid) {
    console.error('❌ VISUAL AID ERROR: visualAid is null or undefined');
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1237">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1238" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1239">
          Visuele hulp ontbreekt
        </p>
      </div>;
  }
  if (!visualAid.type) {
    console.error('❌ VISUAL AID ERROR: visualAid.type is missing', visualAid);
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1240">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1241" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1242">
          Visuele hulp type ontbreekt
        </p>
      </div>;
  }
  if (!visualAid.data) {
    console.error('❌ VISUAL AID ERROR: visualAid.data is missing', visualAid);
    return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1243">
        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1244" />
        <p className="text-sm text-amber-800 font-medium" data-id="element-1245">
          Visuele hulp data ontbreekt
        </p>
      </div>;
  }
  const {
    type,
    data,
    showWhen
  } = visualAid;
  // Check if we should show this visual aid
  if (showWhen === 'correct' && !isCorrect) return null;
  if (showWhen === 'wrong' && isCorrect) return null;
  try {
    switch (type) {
      case 'stairs':
        // Validate required data for stairs
        if (!data.type || !data.from || !data.to) {
          console.error('❌ VISUAL AID ERROR: stairs missing required data', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1246">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1247" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1248">
                Metriek trap: ontbrekende gegevens
              </p>
            </div>;
        }
        return <MetricStairsCompact type={data.type || 'length'} from={data.from} to={data.to} value={data.value} highlight={data.highlight} showAnimation={data.showAnimation} data-id="element-1249" />;
      case 'fraction':
        // Validate required data for fractions
        if (data.usePizza !== false) {
          const numerator = data.numerator || Math.round((data.value || 0) * (data.denominator || 4));
          const denominator = data.denominator || 4;
          if (denominator === 0) {
            console.error('❌ VISUAL AID ERROR: fraction denominator is 0', data);
            return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1250">
                <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1251" />
                <p className="text-sm text-amber-800 font-medium" data-id="element-1252">
                  Breuk: ongeldige noemer (0)
                </p>
              </div>;
          }
          return <FractionPizza numerator={numerator} denominator={denominator} label={data.label} type={data.visualType || 'pizza'} showEaten={data.showEaten} data-id="element-1253" />;
        }
        return <FractionVisual {...data} data-id="element-1254" />;
      case 'geo':
        // Validate required data for geometry
        if (!data.width || !data.height) {
          console.error('❌ VISUAL AID ERROR: geo missing width or height', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1255">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1256" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1257">
                Geometrie: ontbrekende afmetingen
              </p>
            </div>;
        }
        return <GeoGrid {...data} data-id="element-1258" />;
      case 'numberline':
        return <NumberLine {...data} data-id="element-1259" />;
      case 'ratio':
        // Validate required data for ratio table
        if (!data.headers || !data.rows) {
          console.error('❌ VISUAL AID ERROR: ratio missing headers or rows', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1260">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1261" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1262">
                Tabel: ontbrekende gegevens
              </p>
            </div>;
        }
        return <RatioTable {...data} data-id="element-1263" />;
      case 'rule':
        // Validate required data for rule card
        if (!data.title || !data.content) {
          console.error('❌ VISUAL AID ERROR: rule missing title or content', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1264">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1265" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1266">
                Regel: ontbrekende titel of inhoud
              </p>
            </div>;
        }
        return <RuleCard {...data} data-id="element-1267" />;
      case 'money':
        // Validate required data for money visual
        if (data.amount === undefined || data.amount === null) {
          console.error('❌ VISUAL AID ERROR: money missing amount', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1268">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1269" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1270">
                Geld: ontbrekend bedrag
              </p>
            </div>;
        }
        return <MoneyVisual amount={data.amount} breakdown={data.breakdown} showCoins={data.showCoins !== false} showBills={data.showBills !== false} interactive={data.interactive} label={data.label} data-id="element-1271" />;
      case 'column':
        // Validate required data for column visual
        if (!data.operation) {
          console.error('❌ VISUAL AID ERROR: column missing operation', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1272">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1273" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1274">
                Kolomsgewijs: ontbrekende bewerking
              </p>
            </div>;
        }
        const operand1 = data.operand1 ?? data.numbers?.[0];
        const operand2 = data.operand2 ?? data.numbers?.[1];
        if (operand1 === undefined || operand2 === undefined) {
          console.error('❌ VISUAL AID ERROR: column missing operands', data);
          return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1275">
              <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1276" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-1277">
                Kolomsgewijs: ontbrekende getallen
              </p>
            </div>;
        }
        return <ColumnVisual operation={data.operation} operand1={operand1} operand2={operand2} showSteps={data.showSteps !== false} currentStep={data.currentStep} animated={data.animated !== false} onStepChange={data.onStepChange} data-id="element-1278" />;
      default:
        console.warn(`❌ VISUAL AID ERROR: Unknown visual aid type "${type}"`);
        return <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 text-center" data-id="element-1279">
            <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" data-id="element-1280" />
            <p className="text-sm text-amber-800 font-medium" data-id="element-1281">
              Onbekend visueel type: {type}
            </p>
          </div>;
    }
  } catch (error) {
    console.error('❌ VISUAL AID ERROR: Error rendering visual aid', error, visualAid);
    return <div className="p-4 bg-rose-50 rounded-lg border-2 border-rose-200 text-center" data-id="element-1282">
        <AlertTriangle className="w-6 h-6 text-rose-600 mx-auto mb-2" data-id="element-1283" />
        <p className="text-sm text-rose-800 font-medium" data-id="element-1284">
          Fout bij laden visuele hulp
        </p>
        <p className="text-xs text-rose-600 mt-1" data-id="element-1285">
          {error instanceof Error ? error.message : 'Onbekende fout'}
        </p>
      </div>;
  }
}